"use client";

import { PlasFacet } from "@biblioteksentralen/bmdb-search-client";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchState } from "./useSearchState";
import { getCurrentSearchFacets, isWorkSearchFacet, workSearchCategories, WorkSearchFacet } from "./types";
import { getSearchConfig, SiteSearchConfig } from "../../searchApi/searchConfig";
import useSWR from "swr";
import { bmdbFetcher } from "@libry-content/integrations";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { getMediaTypeFilters } from "./useWorksSearch";
import { SearchCategory } from "./SearchContext";

type PlasFacetInUse = Omit<PlasFacet, "type"> & { type: WorkSearchFacet };

export interface FacetAndFilterContextInterface {
  relevantFacets?: PlasFacet[];
  isRelevantFacetsLoading?: boolean;
  cachedCurrentFacet?: PlasFacet;
  setCachedCurrentFacet: Dispatch<SetStateAction<PlasFacet | undefined>>;
  cachedRelevantFacets?: PlasFacet[];
  cacheRelevantFacets: () => void;
  clearFacetCaches: () => void;
  currentFacetsInUse?: PlasFacetInUse[];
  filtersInUse?: Partial<Record<WorkSearchFacet, string[] | undefined>>;
}

const FacetAndFilterContext = createContext<FacetAndFilterContextInterface>({
  setCachedCurrentFacet: () => {},
  cacheRelevantFacets: () => {},
  clearFacetCaches: () => {},
});

export const useFacetAndFilterContext = () => useContext(FacetAndFilterContext);

type FacetAndFilterContextProviderProps = {
  children: ReactNode;
};

export const FacetAndFilterContextProvider = ({ children }: FacetAndFilterContextProviderProps) => {
  const [searchState] = useSearchState();
  const { site } = useCommonData();
  const siteSearchConfig = getSearchConfig(site);

  const workParameter = searchState?.work;
  const searchFacetsInUse = getCurrentSearchFacets(searchState?.selectedCategory);
  const filtersInUse: Partial<Record<WorkSearchFacet, string[] | undefined>> = searchFacetsInUse.reduce(
    (acc, facet) => ({
      ...acc,
      [facet]: workParameter?.[facet],
    }),
    {}
  );

  const {
    facets,
    isLoading: isFacetsLoading,
    newCategory: newMediaType,
  } = useFacetsForCurrentWorkSearch({
    query: searchState?.s,
    mediaType: searchState?.selectedCategory,
    filters: filtersInUse,
    siteSearchConfig,
  });

  //state for facets, to avoid disappearing facets when useFacetsForCurrentWorkSearch updates
  const [relevantFacets, setRelevantFacets] = useState<PlasFacet[]>([]);
  useEffect(() => {
    const filteredFacets = facets?.filter((facet) => facet.terms.length > 0);
    //unset relevantFacets whenever the category changes to a non-work search category
    if (!workSearchCategories.find((workSearchCategory) => workSearchCategory === searchState?.selectedCategory)) {
      setRelevantFacets([]);
      return;
    }
    //set relevantFacets to facets whenever facets get new values
    if (!isFacetsLoading && filteredFacets) setRelevantFacets(filteredFacets);
  }, [isFacetsLoading, setRelevantFacets, facets, searchState?.selectedCategory]);

  //cache for facet currently in use to avoid it updating when it does not make sense (e.g. while inside the facets list of filter terms)
  const [cachedCurrentFacet, setCachedCurrentFacet] = useState<PlasFacet | undefined>(undefined);

  //cache for relevantFacets, used in filter buttons to make facet state not change at unfortunate times
  const [cachedRelevantFacets, setCachedRelevantFacets] = useState<PlasFacet[] | undefined>(undefined);
  const cacheRelevantFacets = () => setCachedRelevantFacets(relevantFacets);

  //currently used search facets with the currently used search terms / filters
  const currentFacetsInUse = (
    relevantFacets.filter(({ type }) => isWorkSearchFacet(type) && !!filtersInUse[type]) as PlasFacetInUse[]
  ).map((facet) => ({
    ...facet,
    terms: facet.terms.filter(
      (term) => isWorkSearchFacet(facet.type) && filtersInUse[facet.type]?.find((termValue) => termValue === term.value)
    ),
  }));

  const clearFacetCaches = useCallback(() => {
    setCachedCurrentFacet(undefined);
    setCachedRelevantFacets(undefined);
  }, [setCachedCurrentFacet, setCachedRelevantFacets]);

  //Empty caches whenever search query is changed
  useEffect(() => {
    clearFacetCaches();
  }, [searchState?.s, clearFacetCaches]);

  //empty caches whenever search category is changed
  useEffect(() => {
    newMediaType && clearFacetCaches();
  }, [newMediaType, clearFacetCaches]);

  return (
    <FacetAndFilterContext.Provider
      value={{
        relevantFacets,
        isRelevantFacetsLoading: isFacetsLoading,
        cachedCurrentFacet,
        setCachedCurrentFacet,
        cachedRelevantFacets: cachedRelevantFacets ?? relevantFacets,
        cacheRelevantFacets,
        clearFacetCaches,
        filtersInUse,
        currentFacetsInUse,
      }}
    >
      {children}
    </FacetAndFilterContext.Provider>
  );
};

type FacetsForCurrentWorkSearchProps = {
  query: string | undefined;
  mediaType: SearchCategory | undefined;
  filters: Partial<Record<WorkSearchFacet, string[] | undefined>>;
  siteSearchConfig: SiteSearchConfig;
};

const useFacetsForCurrentWorkSearch = ({
  query,
  mediaType,
  filters,
  siteSearchConfig,
}: FacetsForCurrentWorkSearchProps): { facets: PlasFacet[] | undefined; isLoading: boolean; newCategory: boolean } => {
  const [previousMediaType, setPreviousMediaType] = useState<string | undefined>(undefined);
  const newCategory = mediaType !== previousMediaType;
  const key = { operation: "checkFacetsQuery", query, mediaType, filters };
  const searchFacetsInUse = getCurrentSearchFacets(mediaType);
  const mediaTypeFilters = key.mediaType ? getMediaTypeFilters(key.mediaType) : [];
  const { data, isLoading } = useSWR(
    key,
    (key) => {
      setPreviousMediaType(key.mediaType);
      if (!key.mediaType) return null;
      return bmdbFetcher({
        operation: "searchWorks",
        params: {
          size: 0,
          query: key?.query,
          filter: { ...key.filters, "plas.mediaTypes": mediaTypeFilters },
          facet: searchFacetsInUse,
          facet_terms_size: 50, // TODO, skulle gjerne vært høyere, men ble ganske laggy ved 100+. Se om vi kan kode dette på en måte som fører til mindre beregninger/lag ved rendering?
        },
        siteSearchConfig,
      });
    },
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  return { facets: data?.facets, isLoading, newCategory };
};
